import React from 'react'

import InternalServerError from '../components/Error/InternalServerError'
import HomePage, { HomePageProps } from '../components/HomePage/HomePage'
import NYLandingPage from '../components/NYLandingPage/NYLanding'
import { logServerSidePropsError } from '../components/utilities/log-helper'
import { Region } from '../config/regions'
import { STATUS_CODE } from '../data/enums/statusCodes'
import fetchHomepageData from '../data/query/homepage/fetchHomepageData'
import fetchNYLandingData, { HomepageNYLandingData } from '../data/query/homepage/fetchNYLandingData'
import { SurrogateKeys } from '../types'

export type CrunchAppProps = {
  NYLandingPageData?: HomepageNYLandingData
  homepageData: HomePageProps
  regionName?: string
  error?: boolean
}

export default ({ NYLandingPageData, homepageData, regionName, error }: CrunchAppProps) => {
  if (error) {
    return <InternalServerError />
  }

  if (NYLandingPageData) {
    return <NYLandingPage {...NYLandingPageData} />
  }

  return <HomePage {...homepageData} regionName={regionName!} />
}

export async function getServerSideProps(context) {
  const regionName = context?.req?.headers?.host?.split('.')?.[0]
  const surrogateKeyHeader = context?.res?.getHeader('Surrogate-Key')?.split(' ')

  try {
    if (regionName === Region.NEW_YORK) {
      context.req.metricRoute = 'NY landing'
      const NYLandingPageResponse = await fetchNYLandingData()

      if (
        NYLandingPageResponse.statusCode !== STATUS_CODE.success &&
        NYLandingPageResponse.statusCode !== STATUS_CODE.notFound
      ) {
        context.res.statusCode = NYLandingPageResponse.statusCode
        context.res.errorMessage = 'NY landing page error'

        return {
          props: { error: true }
        }
      }

      if (NYLandingPageResponse.statusCode === STATUS_CODE.notFound || !NYLandingPageResponse.data) {
        context.res.errorMessage = 'NY landing page data not found'

        return { notFound: true }
      }

      const surrogateKeys = [...surrogateKeyHeader, ...(NYLandingPageResponse.data.entryIDs as SurrogateKeys)]
        .filter(Boolean)
        .join(' ')
      context?.res?.setHeader('Surrogate-Key', surrogateKeys)
      delete NYLandingPageResponse.data.entryIDs

      return {
        props: {
          NYLandingPageData: NYLandingPageResponse.data
        }
      }
    }

    context.req.metricRoute = 'homepage'
    const homepageResponse = await fetchHomepageData(regionName)

    if (homepageResponse.statusCode !== STATUS_CODE.success && homepageResponse.statusCode !== STATUS_CODE.notFound) {
      context.res.statusCode = homepageResponse.statusCode
      context.res.errorMessage = 'Homepage data error'

      return {
        props: { error: true }
      }
    }

    if (homepageResponse.statusCode === STATUS_CODE.notFound || !homepageResponse.data) {
      context.res.errorMessage = 'Homepage data not found'

      return { notFound: true }
    }

    const surrogateKeys = [...surrogateKeyHeader, ...(homepageResponse.data.entryIDs as SurrogateKeys)]
      .filter(Boolean)
      .join(' ')
    context?.res?.setHeader('Surrogate-Key', surrogateKeys)
    context.res.chartbeatType = 'homepage'

    delete homepageResponse.data.entryIDs

    return {
      props: {
        homepageData: homepageResponse.data,
        regionName
      }
    }
  } catch (error) {
    logServerSidePropsError(error)
    context.res.statusCode = STATUS_CODE.internalServerError
    context.res.errorMessage = 'Failed to load homepage data'

    return { props: { error: true } }
  }
}
